import React, { useState } from 'react';
import { Table, Button, Input, Message, Icon, Confirm, Header, Dropdown } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { PricingRule, PartitionRule } from '../../app/models/CompanySportVenueAsset';

interface Props {
  assetId: string;
  partitionRules: PartitionRule[];
  pricingRules: PricingRule[];
  onAdd: (rule: Omit<PricingRule, 'id'>) => void;
  onUpdate: (rule: PricingRule) => void;
  // Use optional string and number to handle unsaved rules safely
  onDelete: (ruleId?: string, capacityUnits?: number) => void;
  isNewAsset: boolean;
}

const PricingRulesSection: React.FC<Props> = ({
  assetId,
  partitionRules,
  pricingRules,
  onAdd,
  onUpdate,
  onDelete,
}) => {
  const { t } = useTranslation();
  const [selectedCapacity, setSelectedCapacity] = useState<number | null>(null);
  const [pricePerHour, setPricePerHour] = useState<number>(0);
  
  // Local state for UI interactions
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editPrice, setEditPrice] = useState<number>(0);
  const [deleteConfirmKey, setDeleteConfirmKey] = useState<string | null>(null);

  // Helper to generate a unique key for the UI
  const getRuleKey = (rule: PricingRule): string => {
    return rule.id || `temp-${rule.capacityUnits}`;
  };

  const handleAdd = () => {
    if (!selectedCapacity || pricePerHour <= 0) return;

    onAdd({
      companySportVenueAssetId: assetId,
      capacityUnits: selectedCapacity,
      pricePerHour,
      isActive: true
    });

    setSelectedCapacity(null);
    setPricePerHour(0);
  };

  const handleUpdate = (rule: PricingRule) => {
    onUpdate({ ...rule, pricePerHour: editPrice });
    setEditingKey(null);
  };

  const handleDelete = () => {
    if (!deleteConfirmKey) return;

    const ruleToDelete = pricingRules.find(r => getRuleKey(r) === deleteConfirmKey);
    
    if (ruleToDelete) {
      // Ensure we pass undefined if id is null for TS compatibility
      onDelete(ruleToDelete.id ?? undefined, ruleToDelete.capacityUnits);
    }
    
    setDeleteConfirmKey(null);
  };

  const availableCapacities = partitionRules
    .filter(pr => pr.isActive)
    .filter(pr => !pricingRules.some(pricingRule => pricingRule.capacityUnits === pr.capacityUnits))
    .map(pr => ({
      key: pr.capacityUnits,
      text: `${pr.capacityUnits} ${t('assetForm.units')}`,
      value: pr.capacityUnits
    }));

  return (
    <div style={{ marginTop: '20px' }}>
      <Header as="h5">
        <Icon name="dollar sign" />
        {t('assetForm.pricingRules')}
      </Header>

      <Message info size="small">
        <p>{t('assetForm.pricingRulesHelp')}</p>
      </Message>

      {/* Add New Rule Section */}
      {availableCapacities.length > 0 && (
        <div style={{ marginBottom: '15px', display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '150px' }}>
            <label style={{ fontSize: '13px', fontWeight: 'bold' }}>{t('assetForm.selectCapacity')}</label>
            <Dropdown
              placeholder={t('assetForm.chooseCapacity')}
              fluid selection
              options={availableCapacities as any}
              value={selectedCapacity as any}
              onChange={(_, data) => setSelectedCapacity(data.value as number)}
            />
          </div>
          <div style={{ flex: 1, minWidth: '150px' }}>
            <label style={{ fontSize: '13px', fontWeight: 'bold' }}>{t('assetForm.pricePerHour')}</label>
            <Input
              type="number" min="0" step="0.01"
              value={pricePerHour || ''}
              onChange={(e) => setPricePerHour(Number(e.target.value))}
              label={{ basic: true, content: '$' }}
              labelPosition="left" fluid
            />
          </div>
          {/* Explicit type="button" to prevent Formik submit */}
          <Button 
            type="button" 
            color="green" 
            icon="add" 
            content={t('common.add')} 
            onClick={handleAdd} 
            disabled={!selectedCapacity || pricePerHour <= 0} 
          />
        </div>
      )}

      {/* Rules Table */}
      {pricingRules.length > 0 ? (
        <Table celled compact>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{t('assetForm.capacity')}</Table.HeaderCell>
              <Table.HeaderCell>{t('assetForm.pricePerHour')}</Table.HeaderCell>
              <Table.HeaderCell width={3}>{t('common.actions')}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {pricingRules
              .sort((a, b) => b.capacityUnits - a.capacityUnits)
              .map((rule) => {
                const ruleKey = getRuleKey(rule);
                const isEditing = editingKey === ruleKey;
                return (
                  <Table.Row key={ruleKey}>
                    <Table.Cell><strong>{rule.capacityUnits}</strong> {t('assetForm.units')}</Table.Cell>
                    <Table.Cell>
                      {isEditing ? (
                        <Input
                          type="number" value={editPrice}
                          onChange={(e) => setEditPrice(Number(e.target.value))}
                          label={{ basic: true, content: '$' }}
                          labelPosition="left" size="small" fluid autoFocus
                        />
                      ) : (
                        <strong>${rule.pricePerHour.toFixed(2)}</strong>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {isEditing ? (
                        <Button.Group size="tiny">
                          <Button type="button" color="green" icon="check" onClick={() => handleUpdate(rule)} />
                          <Button type="button" icon="close" onClick={() => setEditingKey(null)} />
                        </Button.Group>
                      ) : (
                        <Button.Group size="tiny">
                          <Button 
                            type="button" color="blue" icon="edit" 
                            onClick={() => { setEditingKey(ruleKey); setEditPrice(rule.pricePerHour); }} 
                          />
                          <Button 
                            type="button" color="red" icon="trash" 
                            onClick={() => setDeleteConfirmKey(ruleKey)} 
                          />
                        </Button.Group>
                      )}
                    </Table.Cell>
                  </Table.Row>
                );
              })}
          </Table.Body>
        </Table>
      ) : (
        <Message warning content={t('assetForm.noPricingRules')} />
      )}

      <Confirm
        open={deleteConfirmKey !== null}
        header={t('assetForm.deletePricingRule')}
        content={t('assetForm.deletePricingRuleConfirm')}
        onCancel={() => setDeleteConfirmKey(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default PricingRulesSection;